import { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { Divider, Grid } from '@mui/material'

import { USER_CHALLENGE_DEEP_LINK_BASE_URL } from 'config'
import FormSectionContainer from 'components/layout/FormSectionContainer'
import GenericSelect from 'components/form/GenericSelect'
import InputField from 'components/form/InputField'
import HcpTenthsField from 'components/form/HcpTenthsField'
import DateTimePicker from 'components/form/DateTimePicker'
import SwitchToggle from 'components/form/SwitchToggle'

import { selectCountriesWithClubs } from 'store/countries/slice'
import { searchClubs } from 'store/clubs/thunks'
import { selectClubs } from 'store/clubs/slice'
import { selectPrivileges } from 'store/privileges/slice'
import { getPrivileges } from 'store/privileges/thunks'
import { debounce } from 'utils/debounce'
import { UseFormReturn } from 'react-hook-form'
import { ChallengeComputedState, ChallengePrivilege } from 'utils/challenges'
import { GenderType } from 'api/types'
import { rem } from 'theme/materialUITheme'
import { convertCountryIdsToCountries } from 'utils/convertCountryCodesToCountries'
import SearchableClubSelect from '../SearchableClubSelect'
import SearchableCountrySelect from '../SearchableCountrySelect'
import SearchablePrivilegeSelect from '../SearchablePrivilegeSelect'

interface Props {
  form: UseFormReturn<any>
  challenge: Challenge
}

export default function ChallengeFormPhase2({ form, challenge }: Props): JSX.Element {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [initialClubs, setInitialClubs] = useState<SelectOption[]>([])
  const [initialClubsAdded, setInitialClubsAdded] = useState(false)

  const { countries, status: countryStatus } = useSelector(selectCountriesWithClubs)
  const { clubs, status: clubStatus } = useSelector(selectClubs)
  const { privileges, status: privilegeStatus } = useSelector(selectPrivileges)
  const { register, control, setValue, watch } = form

  const selectedCountries = watch(
    'audienceControl.joinAudience.countries',
    challenge?.audienceControl?.joinAudience?.countries ?? [],
  )

  const selectedClubIds = watch(
    'audienceControl.joinAudience.clubIds',
    challenge?.audienceControl?.joinAudience?.clubIds ?? [],
  )

  const locationCountryIsIncluded =
    String(
      watch(
        'audienceControl.joinAudience.countriesInclusive',
        challenge.audienceControl?.joinAudience?.countriesInclusive ?? true,
      ),
    ) !== 'false'
  const isDiscoverable = watch('audienceControl.isDiscoverable', true)
  const useDeepLink = !isDiscoverable

  const hasChallengeStarted =
    !!challenge?.schedule?.startAt && new Date(challenge.schedule.startAt).getTime() <= Date.now()
  const isAudienceTypeLocked =
    challenge.computedState === ChallengeComputedState.COMPLETED ||
    challenge.computedState === ChallengeComputedState.LIVE ||
    (challenge.computedState === ChallengeComputedState.UNPUBLISHED && hasChallengeStarted)

  useEffect(() => {
    if (clubStatus === 'succeeded' && initialClubs.length === 0 && !initialClubsAdded && selectedClubIds.length > 0) {
      const selectedClubsData = clubs.filter((club: Club) => {
        return selectedClubIds.some((selected: SelectOption) => selected.key === club.id)
      })

      const initialClubsValue = selectedClubsData.map((club: Club) => {
        return { key: club.id, label: club.name }
      })

      setInitialClubs(initialClubsValue)
      setInitialClubsAdded(true)
    }
  }, [
    clubStatus,
    selectedClubIds,
    clubs,
    initialClubs,
    initialClubsAdded,
    watch,
    challenge.roundEligibility?.courseIds,
  ])

  useEffect(() => {
    if (privileges.length === 0 && privilegeStatus === 'idle') {
      dispatch(getPrivileges())
    }
  }, [dispatch, privileges, privilegeStatus])

  useEffect(() => {
    if (!useDeepLink) {
      return
    }

    setValue('audienceControl.joinAudience.countries', undefined)
    setValue('audienceControl.joinAudience.clubIds', undefined)
    setValue('audienceControl.joinAudience.privileges', undefined)
    setValue('audienceControl.joinAudience.birthdateFilter.birthdateLowerLimit', undefined)
    setValue('audienceControl.joinAudience.birthdateFilter.birthdateUpperLimit', undefined)
    setValue('audienceControl.joinAudience.hcpFilter.minTenths', null)
    setValue('audienceControl.joinAudience.hcpFilter.maxTenths', null)
    setValue('audienceControl.joinAudience.gender', undefined)
    setValue('audienceControl.useJoinAudienceOnly', false)
  }, [useDeepLink, setValue])

  useEffect(() => {
    if (challenge.audienceControl?.joinAudience?.countriesInclusive && locationCountryIsIncluded) {
      return
    }
    setValue('audienceControl.joinAudience.clubIds', [])
    setValue('audienceControl.joinAudience.privileges', [])
  }, [locationCountryIsIncluded])

  const includesSelectOptions: SelectOption[] = useMemo(
    () => [
      { key: 'true', label: intl.formatMessage({ id: 'strings.includes' }) },
      { key: 'false', label: intl.formatMessage({ id: 'strings.excludes' }) },
    ],
    [intl],
  )

  const genders: SelectOption[] = useMemo(
    () => [
      { key: GenderType.MALE, label: intl.formatMessage({ id: 'strings.male' }) },
      { key: GenderType.FEMALE, label: intl.formatMessage({ id: 'strings.female' }) },
    ],
    [intl],
  )

  const countriesOptions: SelectOption[] = useMemo(
    () => countries.map((country: Country) => ({ key: country.id, label: country.name })),
    [countries],
  )

  const clubsOptions: SelectOption[] = clubs.map((club: Club) => ({ key: club.id, label: club.name }))

  const privilegeOptions: SelectOption[] = useMemo(
    () =>
      privileges
        .filter((privilege: Privilege) => privilege.name in ChallengePrivilege)
        .map((privilege: Privilege) => ({
          key: ChallengePrivilege[privilege.name],
          label: privilege.name,
        })),
    [privileges],
  )

  const searchForClubs = debounce((searchString: string | undefined) => {
    if (searchString && searchString.length >= 3) {
      const countryIds = convertCountryIdsToCountries(
        countries,
        selectedCountries.map((c) => c.key),
      ).map((country) => country.id)

      dispatch(
        searchClubs({
          data: { countryIds, clubName: searchString, activeClubs: true, activeCourses: true },
        }),
      )
    }
  }, 500)

  return (
    <FormSectionContainer>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <h2>{intl.formatMessage({ id: 'strings.visibilityTargeting' })}</h2>
        </Grid>
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={7}>
              <StyledInputfield
                disabled={form.formState.disabled}
                register={register}
                name="deepLink"
                type="text"
                label={intl.formatMessage({ id: 'strings.deepLinkUrl' })}
                outlined
                placeholder={intl.formatMessage({ id: 'strings.deepLink' })}
                value={
                  challenge?.challengeId && challenge.challengeId !== '-1'
                    ? `${USER_CHALLENGE_DEEP_LINK_BASE_URL}/${challenge.token}`
                    : ''
                }
              />
            </Grid>
            <Grid item xs={5}>
              <DeepLinkContainer>
                <h3>{intl.formatMessage({ id: 'strings.onlyVisibleWithLink' })}</h3>
                <SwitchToggle
                  disabled={form.formState.disabled}
                  name="audienceControl.isDiscoverable"
                  value={useDeepLink}
                  onChange={(event) => {
                    const checked = event.target.checked
                    setValue('audienceControl.isDiscoverable', !checked)
                  }}
                />
              </DeepLinkContainer>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={3}>
          <StyledGenericSelect
            name="audienceControl.joinAudience.countriesInclusive"
            label={intl.formatMessage({ id: 'challenges.type' })}
            control={control}
            options={includesSelectOptions}
            disabled={useDeepLink || isAudienceTypeLocked}
            placeholder={intl.formatMessage({ id: 'strings.select' })}
            showResetOption={false}
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <SearchableCountrySelect
                name="audienceControl.joinAudience.countries"
                label={intl.formatMessage({ id: 'strings.countries' })}
                form={form}
                options={countriesOptions}
                status={countryStatus}
                confirmRemoveMessage={
                  challenge?.computedState === ChallengeComputedState.LIVE
                    ? intl.formatMessage({ id: 'challenges.confirmCountryRemove' })
                    : undefined
                }
                disabled={useDeepLink || form.formState.disabled}
              />
            </Grid>
            <Grid item xs={4}>
              <SearchableClubSelect
                name="audienceControl.joinAudience.clubIds"
                label={intl.formatMessage({ id: 'strings.clubs' })}
                form={form}
                options={clubsOptions}
                status={clubStatus}
                {...(initialClubs.length > 0 ? { initialValue: initialClubs } : {})}
                confirmRemoveMessage={
                  challenge?.computedState === ChallengeComputedState.LIVE
                    ? intl.formatMessage({ id: 'challenges.confirmClubRemove' })
                    : undefined
                }
                disabled={
                  useDeepLink ||
                  form.formState.disabled ||
                  watch('audienceControl.joinAudience.countriesInclusive') === 'false'
                }
                search={searchForClubs}
              />
            </Grid>
            <Grid item xs={4}>
              <SearchablePrivilegeSelect
                name="audienceControl.joinAudience.privileges"
                label={intl.formatMessage({ id: 'strings.privileges' })}
                form={form}
                options={privilegeOptions}
                status={privilegeStatus}
                confirmRemoveMessage={
                  challenge?.computedState === ChallengeComputedState.LIVE
                    ? intl.formatMessage({ id: 'challenges.confirmPrivilegesRemove' })
                    : undefined
                }
                disabled={
                  useDeepLink ||
                  form.formState.disabled ||
                  watch('audienceControl.joinAudience.countriesInclusive') === 'false'
                }
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={4}>
          <h2>{intl.formatMessage({ id: 'strings.dateOfBirth' })}</h2>
          <div style={{ marginTop: '24px', display: 'flex' }}>
            <StyledPicker
              name="audienceControl.joinAudience.birthdateFilter.birthdateLowerLimit"
              label={intl.formatMessage(
                { id: 'strings.from' },
                { value: intl.formatMessage({ id: 'strings.dateOfBirth' }) },
              )}
              register={register}
              disabled={useDeepLink || form.formState.disabled}
              required={!!watch('audienceControl.joinAudience.birthdateFilter.birthdateUpperLimit')}
              dateOnly
            />
            <Divider
              sx={{
                marginTop: rem(38),
                marginLeft: rem(6),
                marginRight: rem(6),
                width: rem(10),
                height: rem(2),
                backgroundColor: '#373D40',
              }}
            />
            <StyledPicker
              name="audienceControl.joinAudience.birthdateFilter.birthdateUpperLimit"
              label={intl.formatMessage(
                { id: 'strings.to' },
                { value: intl.formatMessage({ id: 'strings.dateOfBirth' }) },
              )}
              register={register}
              disabled={useDeepLink || form.formState.disabled}
              required={!!watch('audienceControl.joinAudience.birthdateFilter.birthdateLowerLimit')}
              dateOnly
            />
          </div>
        </Grid>
        <Grid item xs={4}>
          <h2>{intl.formatMessage({ id: 'strings.gender' })}</h2>
          <div style={{ marginTop: '30px' }}>
            <StyledGenericSelect
              name="audienceControl.joinAudience.gender"
              label={intl.formatMessage({ id: 'strings.gender' })}
              placeholder={intl.formatMessage({ id: 'strings.select' })}
              control={control}
              options={genders}
              disabled={useDeepLink || form.formState.disabled}
              initialValue={watch('audienceControl.joinAudience.gender') || undefined}
            />
          </div>
        </Grid>
        <Grid item xs={4}>
          <h2>{intl.formatMessage({ id: 'strings.handicap' })}</h2>
          <HcpRange>
            <HcpTenthsField
              control={control}
              name="audienceControl.joinAudience.hcpFilter.maxTenths"
              placeholder={intl.formatMessage({ id: 'strings.minHcp' })}
              required={!!watch('audienceControl.joinAudience.hcpFilter.minTenths')}
              disabled={useDeepLink || form.formState.disabled}
            />
            <Divider
              sx={{
                flex: '0 0 auto',
                width: rem(10),
                height: rem(2),
                margin: rem(6),
                backgroundColor: '#373d40',
              }}
            />
            <HcpTenthsField
              control={control}
              name="audienceControl.joinAudience.hcpFilter.minTenths"
              placeholder={intl.formatMessage({ id: 'strings.maxHcp' })}
              required={!!watch('audienceControl.joinAudience.hcpFilter.maxTenths')}
              disabled={useDeepLink || form.formState.disabled}
            />
          </HcpRange>
        </Grid>
      </Grid>
    </FormSectionContainer>
  )
}

const HcpRange = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${rem(40)};

  & .MuiFormControl-root {
    margin: 0;
    align-self: center;
  }
`

const DeepLinkContainer = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
  width: 80%;
  height: 100%;
  margin-left: 30px;

  & h3 {
    font-weight: 600;
    margin-right: 10px;
  }

  & .MuiFormControl-root {
    margin: 0;
    width: 60%;
  }
`

const StyledInputfield = styled(InputField)`
  & .MuiOutlinedInput-root {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    & fieldset {
      border-color: #e4e4e7;
    }

    &:hover fieldset {
      border-color: #e4e4e7;
    }

    &.Mui-focused fieldset {
      border-color: #e4e4e7;
    }
  }

  & .MuiOutlinedInput-input {
    padding: 10px 12px;
  }

  & .MuiSelect-icon {
    color: #8a8a8e;
    font-size: 20px;
  }
`
const StyledGenericSelect = styled(GenericSelect)`
  & .MuiOutlinedInput-root {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    & fieldset {
      border-color: #e4e4e7;
    }

    &:hover fieldset {
      border-color: #e4e4e7;
    }

    &.Mui-focused fieldset {
      border-color: #e4e4e7;
    }
  }

  & .MuiOutlinedInput-input {
    padding: 10px 12px;
  }

  & .MuiSelect-icon {
    color: #8a8a8e;
    font-size: 20px;
  }
`
const StyledPicker = styled(DateTimePicker)`
  margin-top: 15px;
  width: 100%;
  & div:before,
  .MuiInput-underline:hover:before {
    border-bottom: none;
  }

  & .MuiInput-underline:hover {
    & input {
      border-color: #e4e4e7;
    }
  }

  & input {
    border: 1px solid;
    border-color: #e4e4e7;
    border-radius: 8px;
    padding: 16px 14px;
  }
  & label {
    transition: none;
    transform: none;
    margin-top: -20px;
  }

  & .MuiOutlinedInput-root {
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    & fieldset {
      border-color: #e4e4e7;
    }

    &:hover fieldset {
      border-color: #e4e4e7;
    }

    &.Mui-focused fieldset {
      border-color: #e4e4e7;
    }
  }

  & .MuiOutlinedInput-input {
    padding: 10px 12px;
  }
`
